import api from './api';

/** وضعیت‌های درخواست خرید (مارکت‌پلیس معکوس) */
export type BuyerRequestStatus =
  | 'pending_review'
  | 'active'
  | 'offer_accepted'
  | 'converted'
  | 'expired'
  | 'cancelled'
  | 'rejected';

/** وضعیت‌های پیشنهاد فروشنده روی درخواست */
export type BuyerRequestOfferStatus =
  | 'active'
  | 'withdrawn'
  | 'accepted'
  | 'declined'
  | 'released'
  | 'expired'
  | 'completed';

export interface BuyerRequestItem {
  _id: string;
  title: string;
  description?: string;
  accountTypeId?: { _id?: string; name?: string; image?: string } | string | null;
  categoryId?: { _id?: string; name?: string; slug?: string } | string | null;
  userId?: { _id?: string; name?: string; phone?: string; email?: string } | string | null;
  budgetMin?: number;
  budgetMax?: number;
  quantity: number;
  fields?: Record<string, any>;
  status: BuyerRequestStatus;
  expiresAt?: string;
  offersCount?: number;
  lowestOfferPrice?: number;
  views?: number;
  rejectionReason?: string;
  reviewedBy?: { _id?: string; name?: string } | string | null;
  reviewedAt?: string;
  paymentDeadline?: string;
  orderId?: { _id?: string; orderNumber?: string; status?: string; totalAmount?: number } | string | null;
  createdAt?: string;
}

export interface BuyerRequestOffer {
  _id: string;
  storeId?: { _id?: string; name?: string; slug?: string; logo?: string; rating?: number } | string | null;
  sellerUserId?: { _id?: string; name?: string; phone?: string } | string | null;
  price: number;
  deliveryHours?: number;
  message?: string;
  availability?: string;
  status: BuyerRequestOfferStatus;
  createdAt?: string;
}

export interface BuyerRequestDetail extends BuyerRequestItem {
  offers: BuyerRequestOffer[];
}

export interface BuyerRequestsQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface PaginatedBuyerRequests {
  items: BuyerRequestItem[];
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
}

const buyerRequestService = {
  /** لیست درخواست‌های خرید (خروجی mongoose-paginate: docs/totalDocs/totalPages/page) */
  async getRequests(query: BuyerRequestsQuery = {}): Promise<PaginatedBuyerRequests> {
    const params: any = {};
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.search) params.search = query.search;
    if (query.status) params.status = query.status;

    // api.get پاکت { status:'success', data } را باز می‌کند و data را برمی‌گرداند
    const raw: any = await api.get<any>('/admin/buyer-requests', { params } as any);

    // خروجی mongoose-paginate: { docs, totalDocs, totalPages, page, limit }
    if (raw && Array.isArray(raw.docs)) {
      return {
        items: raw.docs,
        meta: {
          totalItems: Number(raw.totalDocs || 0),
          itemCount: raw.docs.length,
          itemsPerPage: Number(raw.limit || params.limit || 10),
          totalPages: Number(raw.totalPages || 1),
          currentPage: Number(raw.page || params.page || 1),
        },
      };
    }

    // Fallback اگر ساختار دیگری برگشت
    if (raw && Array.isArray(raw.items) && raw.meta) {
      return raw as PaginatedBuyerRequests;
    }

    return {
      items: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: Number(params.limit || 10),
        totalPages: 0,
        currentPage: Number(params.page || 1),
      },
    };
  },

  /** جزئیات کامل درخواست + پیشنهادهای فروشندگان */
  async getRequestDetail(id: string): Promise<BuyerRequestDetail> {
    const raw: any = await api.get<any>(`/admin/buyer-requests/${id}`);
    if (raw && raw._id) return raw as BuyerRequestDetail;
    if (raw && raw.data?._id) return raw.data as BuyerRequestDetail;
    throw new Error('پاسخ نامعتبر از سرور');
  },

  /** تأیید و انتشار (فقط وقتی status=pending_review) — برای ۷ روز منتشر می‌شود */
  async approve(id: string): Promise<BuyerRequestItem> {
    const res = await api.post<any>(`/admin/buyer-requests/${id}/approve`);
    if (res && res.status === 'success' && res.data) return res.data as BuyerRequestItem;
    return res as BuyerRequestItem;
  },

  /** رد درخواست — دلیل الزامی است */
  async reject(id: string, reason: string): Promise<BuyerRequestItem> {
    const res = await api.post<any>(`/admin/buyer-requests/${id}/reject`, { reason });
    if (res && res.status === 'success' && res.data) return res.data as BuyerRequestItem;
    return res as BuyerRequestItem;
  },

  /** بستن اجباری درخواست‌های pending/active — دلیل اختیاری */
  async close(id: string, reason?: string): Promise<BuyerRequestItem> {
    const body = reason && reason.trim() ? { reason: reason.trim() } : {};
    const res = await api.post<any>(`/admin/buyer-requests/${id}/close`, body);
    if (res && res.status === 'success' && res.data) return res.data as BuyerRequestItem;
    return res as BuyerRequestItem;
  },
};

export default buyerRequestService;
